import React from 'react';
import { useNavigate } from 'react-router-dom';

import classnames from 'classnames/bind';

import ROUTES from '@CONSTANTS/ROUTES.constant';
import GenerationSteps from '@CONSTANTS/GENERATION_STEPS.constant';

import { BrandStep, BrandStepData } from '@REDUCERS';

import useDocumentGenerationSteps from '@HOOKS/store/useDocumentGenerationSteps';
import { useGetAllBrands } from '@SERVICES';

import Grid from '@COMPONENTS/SHARED/Grid';
import SquareButton from '@COMPONENTS/COMMON/buttons/SquareButton';
import DataLoader from '@COMPONENTS/SHARED/DataLoader';
import EmptyStateHint from '@COMPONENTS/SHARED/EmptyStateHint';
import SearchbarInput from '@COMPONENTS/COMMON/inputs/input/SearchInput';
import StepNavigation from '@COMPONENTS/COMMON/navigation/StepNavigation';

import styles from './Brand.module.scss';

const cx: CX = classnames.bind(styles);

function BrandPage() {
    const {
        actions: documentGenerationStepsActions,
        services: documentGenerationStepsServices,
    } = useDocumentGenerationSteps(() => true);

    const {
        data: brandsData = [],
        isLoading,
        isError,
        error,
    } = useGetAllBrands();
    const [brandName, setBrandName] = React.useState('');

    const navigate = useNavigate();

    const brandStep: BrandStep = documentGenerationStepsServices.getStepData(GenerationSteps.BRAND);
    const hasSelectedBrand = brandStep.isComplete;

    const filterList = React.useMemo(() => {
        const lowerCaseBrandName = brandName.toLowerCase();
        const filteredBrands = brandsData.filter(({ name }: { name: string }) => name.toLowerCase().includes(lowerCaseBrandName));

        // Define the type for the accumulator
        type ContractTypeMap = {
            [key: string]: typeof filteredBrands;
        };

        return filteredBrands.reduce<ContractTypeMap>((acc, cur) => {
            const contractType = cur.contract_types || 'Default Contract Type';

            if (!acc[contractType]) {
                acc[contractType] = [];
            }

            acc[contractType].push(cur);

            return acc;
        }, {} as ContractTypeMap);
    }, [brandName, brandsData]);

    function handleClick(id: number, name: string) {
        const data: BrandStepData = {
            id,
            name,
        };

        documentGenerationStepsActions.apply({
            id: GenerationSteps.BRAND,
            isComplete: true,
            data,
        });

        navigate(ROUTES.location.path);
    }

    function getHighlighted(id: number) {
        const step: BrandStep = documentGenerationStepsServices.getStepData(
            GenerationSteps.BRAND,
        );

        return step.data?.id === id;
    }

    const handleNext = () => {
        if (hasSelectedBrand) {
            navigate(ROUTES.location.path);
        }
    };

    return (
        <div className={cx('page')}>
            <div className={cx('brand-header')}>
                <h1>Brand</h1>
                <div className={cx('brand-search')}>
                    <SearchbarInput
                        isLoading={false}
                        placeholder="Search brands..."
                        value={brandName}
                        onChange={(val: string) => {
                            setBrandName(val);
                        }}
                        onFocus={() => {}}
                        iconWrapper="icon-wrapper"
                        inputWrapper="input-wrapper"
                    />
                </div>
            </div>

            <DataLoader
                isLoading={isLoading}
                isError={isError}
                error={error}
            >
                {brandsData.length === 0 ? (
                    <EmptyStateHint
                        type="brands"
                        title="No Brands Found"
                        description="It looks like there are no brands configured yet. Brands help identify and customize documents for different companies or organizations. You can add new brands in the app settings."
                    />
                ) : (
                    Object.keys(filterList).map((contractType) => (
                        <div key={contractType}>
                            <h3 className={cx('title-wrapper')}>{contractType}</h3>
                            <Grid
                                additionalClassName={cx('brand-data')}
                                gridChildClassName={cx('brand-child')}
                            >
                                {filterList[contractType].map((brand) => {
                                    const highlighted = getHighlighted(brand.id);
                                    return (
                                        <SquareButton
                                            key={brand.id}
                                            highlighted={highlighted}
                                            title={brand.name}
                                            onClick={() => handleClick(brand.id, brand.name)}
                                            img={brand.logo_svg || ''}
                                        />
                                    );
                                })}
                            </Grid>
                        </div>
                    ))
                )}
            </DataLoader>

            <StepNavigation
                onNext={handleNext}
                nextDisabled={!hasSelectedBrand}
                showNext={hasSelectedBrand}
                nextText="Continue to Location"
                sticky
            />
        </div>
    );
}

export default BrandPage;
